/**
 * @fileoverview Single source of copy for the Flex marketing landing page.
 *
 * Marketing edits text here; no component hardcodes a string. Copy comes from the
 * standalone package they delivered (see the design doc) and is English only.
 */

export type TLandingCta = { label: string; href: string; analyticsId: string; ariaLabel: string };
export type TLandingMetric = { value: string; label: string };
export type TLandingCapability = { number: string; title: string; description: string };
export type TLandingLink = { label: string; href: string };

const CASE_STUDIES_URL = 'https://www.attend.tech/case-studies';
const CONTACT_URL = 'https://www.attend.tech/contact-us';

export const landingCtas = {
  // `ariaLabel` exists because the same two labels repeat three times on the page: search
  // engines and screen readers both flag repeated generic link text, and the visible wording
  // is marketing's to keep.
  learnMore: {
    label: 'Learn More',
    href: CASE_STUDIES_URL,
    analyticsId: 'learn_more',
    ariaLabel: 'Learn more about Flex in Attend case studies',
  },
  contactUs: {
    label: 'Contact Us',
    href: CONTACT_URL,
    analyticsId: 'contact_us',
    ariaLabel: 'Contact Attend about Flex',
  },
} satisfies Record<string, TLandingCta>;

export const landingContent = {
  hero: {
    eyebrow: "Attend's Flex Product",
    title: 'Ticket plans built for fans.',
    lede:
      'Flex powers customizable bundles for tickets, suites, and experiences. Our Flex product is ' +
      'our fastest-growing product and the options are endless - teams, venues, and entertainment ' +
      'providers are building the most innovative programs in the live event industry with Flex.',
    imageAlt: 'Hat Trick Plan powered by Flex displayed on desktop and mobile',
  },
  /** Alt text for the brand mark. The asset is Attend's white wordmark, not a Flex symbol. */
  brandLogoAlt: 'Attend',
  /** Accessible names for sections whose heading is not visible on screen. */
  sectionLabels: {
    metrics: 'Flex performance metrics',
    footerNav: 'Legal',
  },
  metrics: [
    { value: '$2,000', label: 'Average Order Value' },
    { value: '78%', label: 'Users on Mobile' },
    { value: '$600K', label: 'Average Program Revenue' },
  ] satisfies TLandingMetric[],
  capabilities: {
    eyebrow: 'What Flex can do',
    title: 'One flexible platform. More ways to sell.',
    description:
      'Give customers control over what they buy while keeping the experience fast, branded, and ' +
      'built for conversion.',
    cards: [
      {
        number: '01',
        title: 'Custom ticket plans',
        description: 'Let fans build plans around the games and events that fit their schedule.',
      },
      {
        number: '02',
        title: 'Ticketing integrations',
        description: 'Connect the purchase experience with your existing ticketing platform.',
      },
      {
        number: '03',
        title: 'Premium inventory',
        description: 'Bring suites and other premium products online in an intuitive buying flow.',
      },
      {
        number: '04',
        title: 'Integrated add-ons',
        description: 'Bundle tickets with upgrades and extras that increase order value.',
      },
      {
        number: '05',
        title: 'Built-in controls',
        description: 'Use account gating and fraud prevention tools to protect inventory.',
      },
      {
        number: '06',
        title: 'Real-time sales data',
        description:
          'Give your team the information they need to understand demand and act quickly.',
      },
    ] satisfies TLandingCapability[],
  },
  story: {
    eyebrow: 'Built for fans.',
    title: 'Launch custom plans built for every customer segment.',
    description:
      'Flex supports highly customized programs across sports, entertainment, and live events while ' +
      'keeping the purchase journey simple for fans.',
  },
  finalCta: { eyebrow: 'New fans. New revenue.', title: 'See what you can build with Flex.' },
  footer: {
    brand: 'Flex',
    description: 'Flexible ticketing experiences for live events.',
    copyright: '© Attend',
    links: [
      { label: 'Privacy Policy', href: 'https://www.attend.tech/privacy-policy' },
      { label: 'Terms of Use', href: 'https://www.attend.tech/terms-of-use' },
      {
        label: 'Marketplace Purchase Policy',
        href: 'https://www.attend.tech/marketplace-purchase-policy',
      },
      { label: 'Contact', href: 'https://www.attend.tech/contact-us' },
    ] satisfies TLandingLink[],
  },
};
